import { Link } from "react-router-dom";
import { GraduationCap, Quote, ArrowRight, Award, BookOpen, ShieldCheck } from "lucide-react";
import PageWrapper from "@/components/layout/PageWrapper";
import Breadcrumb from "@/components/Breadcrumb";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES, CONTACT } from "@/data/content";

const Principal = () => {
  const { t } = useLanguage();
  return (
    <PageWrapper
      title={`${t("principal.name")} — Principal, ADHD School Bhadrak`}
      description="Meet Swasti Prava Jena, Principal & Founder of Aachary Dev Development Holistic School (ADHD School), Bhadrak."
    >
      <section className="adhd-container pt-10 lg:pt-14">
        <Breadcrumb pageKey="nav.principal" />
      </section>

      <section className="adhd-container mt-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-[color:var(--adhd-border)]">
            <img
              src={IMAGES.principal}
              alt={t("principal.name")}
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="mt-6 adhd-card p-6">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl" style={{ background: "rgba(230,194,41,0.18)" }}>
                <Award size={18} style={{ color: "#b58c00" }} />
              </span>
              <p className="font-serif text-lg text-[color:var(--adhd-text)]">Certifications</p>
            </div>
            <ul className="mt-4 flex flex-col gap-2 text-sm text-[color:var(--adhd-text)]/85">
              <li>• ADHD Management</li>
              <li>• Autism Support and Care</li>
              <li>• Nursery Teacher Training (NTT)</li>
              <li>• Nursing Trained Teacher</li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <p className="adhd-eyebrow">
            <GraduationCap size={13} /> {t("principal.title")}
          </p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-[color:var(--adhd-text)]">
            {t("principal.name")}
          </h1>
          <p className="mt-2 text-[color:var(--adhd-muted)] text-lg">{t("principal.role")}</p>

          <div className="mt-6 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <BookOpen size={18} className="mt-1" style={{ color: "var(--adhd-primary)" }} />
              <p className="text-sm text-[color:var(--adhd-text)]/85">
                <span className="font-semibold">Qualifications: </span>
                {t("principal.qualifications")}
              </p>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="mt-1" style={{ color: "var(--adhd-success)" }} />
              <p className="text-sm text-[color:var(--adhd-text)]/85">
                <span className="font-semibold">Certifications: </span>
                {t("principal.certs")}
              </p>
            </div>
          </div>

          <blockquote
            className="mt-8 relative adhd-card-soft p-8 italic text-lg leading-relaxed"
          >
            <Quote
              size={28}
              className="absolute -top-3 -left-3 opacity-30"
              style={{ color: "var(--adhd-primary)" }}
            />
            "{t("principal.quote")}"
          </blockquote>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="principal-meet-cta" className="adhd-btn-primary">
              Meet the Principal in Person <ArrowRight size={14} />
            </Link>
            <a
              href={`tel:${CONTACT.phoneTel}`}
              className="adhd-btn-secondary"
              data-testid="principal-call"
            >
              {t("cta.call")}
            </a>
          </div>
        </div>
      </section>

      <section className="adhd-section">
        <div className="adhd-container">
          <div className="adhd-card-soft p-8 sm:p-12 grid lg:grid-cols-3 gap-8">
            {[
              { t: "Patience", d: "Every child has their own pace. We meet them where they are." },
              { t: "Respect", d: "Children are treated as whole human beings — never reduced to a diagnosis." },
              { t: "Partnership", d: "Parents are our co-pilots. We grow with you, not just your child." },
            ].map((v) => (
              <div key={v.t}>
                <p className="font-serif text-2xl text-[color:var(--adhd-text)]">{v.t}</p>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--adhd-muted)]">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default Principal;
